import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject
} from "react";
import clsx from "clsx";
import {
  Check,
  Heart,
  Headphones,
  ListMusic,
  Music2,
  Newspaper,
  Pause,
  Play,
  Podcast,
  Radio,
  SkipBack,
  SkipForward,
  Sparkles
} from "lucide-react";
import {
  AUDIO_COMPANION_ANSWER,
  AUDIO_COMPANION_GENERATION_STEPS,
  AUDIO_COMPANION_GREETING,
  AUDIO_COMPANION_P1_PROGRAM_IDS,
  AUDIO_COMPANION_SIMILAR_RECOMMENDATION,
  AUDIO_COMPANION_SIMILAR_PROGRAM_IDS,
  AUDIO_COMPANION_WAITING_SONG,
  getAudioCompanionProgram,
  type AudioCompanionDemoState,
  type AudioCompanionProgram,
  type AudioCompanionProgramId
} from "../../config/audioCompanionDemo";
import { assetUrl } from "../../utils/assetUrl";
import { claimAudioPlayback, type AudioPlaybackClaim } from "../../utils/audioPlaybackCoordinator";
import "./NightAudioCompanionDemo.css";

const AUDIO_COMPANION_EVA_HEADPHONES_SRC = assetUrl(
  "assets/figma/night-ai/generated-icons/audio-companion-eva-headphones.png"
);
const AUDIO_COMPANION_RECOMMEND_ICON_SRC_BY_PROGRAM: Partial<Record<AudioCompanionProgramId, string>> = {
  "tech-morning": assetUrl("assets/figma/night-ai/audio-companion-icons/recommend-tech-podcast.jpg"),
  "commute-playlist": assetUrl("assets/figma/night-ai/audio-companion-icons/recommend-commute-playlist.jpg"),
  "market-brief": assetUrl("assets/figma/night-ai/audio-companion-covers/market-brief.webp")
};
const AUDIO_COMPANION_RECOMMENDATION_CARDS: Partial<Record<AudioCompanionProgramId, {
  badge: string;
  category: string;
  details: string[];
  title: string;
}>> = {
  "tech-morning": {
    title: "AI 播客",
    category: "AI 播客",
    details: ["英伟达财报 · 美联储动态 · A股早报", "为你定制 · 10分钟 · 通勤专属"],
    badge: "10 min"
  },
  "commute-playlist": {
    title: "欧美精选 · 继续听",
    category: "音乐",
    details: ["昨日播放到 Cruel Summer", "续播 · AI音效带来最佳体验"],
    badge: "续播"
  },
  "market-brief": {
    title: "开盘前 5 分钟",
    category: "快讯快报",
    details: ["财经和半导体速览", "开盘前补齐市场背景"],
    badge: "5 min"
  }
};
const AUDIO_COMPANION_GENERATION_TOTAL_MS =
  AUDIO_COMPANION_GENERATION_STEPS[AUDIO_COMPANION_GENERATION_STEPS.length - 1]?.delayMs ?? 6500;
const AUDIO_COMPANION_WAITING_MUSIC_FALLBACK_MS = 6000;

function getAudioRecommendationCopy(program: AudioCompanionProgram) {
  const recommendationCopy = AUDIO_COMPANION_RECOMMENDATION_CARDS[program.id];

  return {
    badge: recommendationCopy?.badge ?? program.durationLabel,
    category: recommendationCopy?.category ?? program.category,
    details: recommendationCopy?.details ?? [program.subtitle, program.description],
    title: recommendationCopy?.title ?? program.title
  };
}

function getAudioProgramSubtitleSource(program: AudioCompanionProgram) {
  const displayCopy = getAudioRecommendationCopy(program);
  const shouldMirrorCardCopy =
    Boolean(AUDIO_COMPANION_RECOMMENDATION_CARDS[program.id]) ||
    AUDIO_COMPANION_SIMILAR_PROGRAM_IDS.includes(program.id);

  if (shouldMirrorCardCopy) {
    return displayCopy.details.join("。");
  }

  return program.spokenText || program.description;
}

interface NightAudioCompanionDemoProps {
  state: AudioCompanionDemoState;
  onAnswerFinished: () => void;
  onBackToRecommend: () => void;
  onExpandPreview: () => void;
  onFallbackSpeak: (text: string) => void;
  onPreviewProgram: (programId: AudioCompanionProgramId) => void;
  onSelectProgram: (programId: AudioCompanionProgramId) => void;
  onStartGeneratedProgram: () => void;
}

export function NightAudioCompanionDemo({
  state,
  onAnswerFinished,
  onBackToRecommend,
  onExpandPreview,
  onFallbackSpeak,
  onPreviewProgram,
  onSelectProgram,
  onStartGeneratedProgram
}: NightAudioCompanionDemoProps) {
  const selectedProgram = getAudioCompanionProgram(state.selectedProgramId);
  const waitingMusicRef = useRef<HTMLAudioElement | null>(null);
  const waitingMusicClaimRef = useRef<AudioPlaybackClaim | null>(null);
  const waitingMusicFadeTimerRef = useRef<number | null>(null);
  const waitingSongUrl = useMemo(() => assetUrl(AUDIO_COMPANION_WAITING_SONG.audioSrc, { versioned: false }), []);

  const clearWaitingMusicFade = useCallback(() => {
    if (waitingMusicFadeTimerRef.current === null) {
      return;
    }

    window.clearInterval(waitingMusicFadeTimerRef.current);
    waitingMusicFadeTimerRef.current = null;
  }, []);

  const stopWaitingMusic = useCallback(() => {
    clearWaitingMusicFade();
    releasePlaybackClaim(waitingMusicClaimRef);

    const audio = waitingMusicRef.current;

    if (!audio) {
      return;
    }

    audio.pause();
    audio.muted = true;
    audio.volume = 0;

    try {
      audio.currentTime = 0;
    } catch {
      // The media element may not have metadata yet when a failed prearm is cleaned up.
    }
  }, [clearWaitingMusicFade]);

  const activateWaitingMusic = useCallback(() => {
    const audio = waitingMusicRef.current;

    if (!audio) {
      return false;
    }

    releasePlaybackClaim(waitingMusicClaimRef);
    waitingMusicClaimRef.current = claimAudioPlayback(stopWaitingMusic);
    audio.muted = false;

    if (audio.paused) {
      void audio.play().catch(() => stopWaitingMusic());
    }

    fadeAudioVolume(audio, 0.42, 520, waitingMusicFadeTimerRef);
    return true;
  }, [stopWaitingMusic]);

  useEffect(() => () => stopWaitingMusic(), [stopWaitingMusic]);

  return (
    <section
      className={clsx("night-audio-demo", `night-audio-demo--${state.stage}`)}
      aria-label="音乐搭子音频演示"
    >
      <audio
        ref={waitingMusicRef}
        src={waitingSongUrl}
        preload="auto"
        onEnded={stopWaitingMusic}
        onError={stopWaitingMusic}
      />

      {state.stage === "p1_recommend" || state.stage === "p2_preview" ? (
        <AudioRecommendationPanel
          collapsed={state.stage === "p2_preview"}
          selectedProgramId={state.stage === "p2_preview" ? state.selectedProgramId : undefined}
          onSelectProgram={onPreviewProgram}
        />
      ) : null}

      {state.stage !== "p1_recommend" ? (
        <AudioCollapsedHomeCard
          background={state.stage !== "p2_preview"}
          onOpen={onBackToRecommend}
        />
      ) : null}

      {state.stage === "p2_preview" ? (
        <AudioPreviewDock
          program={selectedProgram}
          onExpand={onExpandPreview}
        />
      ) : null}

      {state.stage === "p2_playing" ? (
        <AudioPlayerPanel
          program={selectedProgram}
          onBack={onBackToRecommend}
          onSelectProgram={onSelectProgram}
        />
      ) : null}

      {state.stage === "p3_answer" ? (
        <AudioAnswerPanel
          onFallbackSpeak={onFallbackSpeak}
          onFinished={onAnswerFinished}
        />
      ) : null}

      {state.stage === "p4_similar" ? (
        <AudioSimilarPanel onFallbackSpeak={onFallbackSpeak} onSelectProgram={onSelectProgram} />
      ) : null}

      {state.stage === "p5_generating" ? (
        <AudioGenerationPanel
          introFinished={state.generationIntroFinishedRunId === state.generationRunId}
          onActivateWaitingMusic={activateWaitingMusic}
          runId={state.generationRunId}
          onStartGeneratedProgram={onStartGeneratedProgram}
          onStopWaitingMusic={stopWaitingMusic}
        />
      ) : null}
    </section>
  );
}

function AudioCollapsedHomeCard({
  background = false,
  onOpen
}: {
  background?: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      className={clsx(
        "night-audio-collapsed-card",
        background && "night-audio-collapsed-card--background"
      )}
      type="button"
      aria-label="展开音乐搭子推荐"
      disabled={background}
      onClick={onOpen}
    >
      <span className="night-audio-collapsed-card__surface" aria-hidden="true" />
      <span className="night-audio-collapsed-card__icon night-audio-collapsed-card__icon--violet" aria-hidden="true">
        <AudioProgramCardIcon programId="tech-morning" />
      </span>
      <span className="night-audio-collapsed-card__copy">
        <strong>音乐搭子</strong>
        <span>通勤内容推荐已就绪</span>
      </span>
    </button>
  );
}

function AudioRecommendationPanel({
  collapsed = false,
  selectedProgramId,
  onSelectProgram
}: {
  collapsed?: boolean;
  selectedProgramId?: AudioCompanionProgramId;
  onSelectProgram: (programId: AudioCompanionProgramId) => void;
}) {
  const programs = AUDIO_COMPANION_P1_PROGRAM_IDS.map(getAudioCompanionProgram);

  return (
    <article
      className={clsx("night-audio-recommend-panel", collapsed && "night-audio-recommend-panel--collapsed")}
      aria-hidden={collapsed ? "true" : undefined}
      aria-label="通勤内容推荐"
    >
      <span className="night-audio-recommend-panel__surface" aria-hidden="true" />
      <div className="night-audio-recommend-panel__header">
        <h2>{AUDIO_COMPANION_GREETING}</h2>
        <p>识别到早高峰上车，EVA 已为你筛好今天路上适合听的内容。</p>
      </div>

      <div className="night-audio-recommend-panel__status" aria-label="通勤场景状态">
        <span className="night-audio-recommend-panel__ai-mark">AI</span>
        <span>08:15 通勤内容推荐已就绪</span>
      </div>

      <div className="night-audio-recommend-panel__cards">
        {programs.map((program) => {
          const card = getAudioRecommendationCopy(program);
          const recommendIconSrc = AUDIO_COMPANION_RECOMMEND_ICON_SRC_BY_PROGRAM[program.id] ?? assetUrl(program.coverSrc);

          return (
            <button
              key={program.id}
              className={clsx(
                "night-audio-program-card",
                `night-audio-program-card--${program.accent}`,
                selectedProgramId === program.id && "night-audio-program-card--selected"
              )}
              type="button"
              onClick={() => onSelectProgram(program.id)}
            >
              <span
                className={clsx(
                  "night-audio-program-card__cover",
                  `night-audio-program-card__cover--${program.accent}`
                )}
                aria-hidden="true"
              >
                <img
                  className="night-audio-program-card__cover-image"
                  src={recommendIconSrc}
                  alt=""
                  draggable="false"
                />
              </span>
              <span className="night-audio-program-card__copy">
                <strong>{card.title}</strong>
                <span className="night-audio-program-card__details">
                  {card.details.map((detail) => (
                    <span key={detail}>
                      {detail}
                    </span>
                  ))}
                </span>
              </span>
              <span className="night-audio-program-card__duration" aria-hidden="true">
                {card.badge}
              </span>
            </button>
          );
        })}
      </div>
    </article>
  );
}

function AudioProgramCardIcon({
  programId
}: {
  programId: AudioCompanionProgramId;
}) {
  const iconProps = {
    size: 44,
    strokeWidth: 1.9
  };

  if (programId === "commute-playlist") {
    return <Headphones {...iconProps} />;
  }

  if (programId === "market-brief") {
    return <Radio {...iconProps} />;
  }

  if (programId === "creator-economy") {
    return <Newspaper {...iconProps} />;
  }

  if (programId === "ai-hardware-daily") {
    return <Radio {...iconProps} />;
  }

  return <Podcast {...iconProps} />;
}

function AudioProgramArtwork({
  compact = false,
  program
}: {
  compact?: boolean;
  program: AudioCompanionProgram;
}) {
  return (
    <span
      className={clsx("night-audio-artwork", compact && "night-audio-artwork--compact")}
      aria-hidden="true"
    >
      <img src={assetUrl(program.coverSrc)} alt="" draggable={false} />
      <span className="night-audio-artwork__shade" />
      <AudioProgramCardIcon programId={program.id} />
      <strong>{program.category}</strong>
    </span>
  );
}

function AudioPreviewDock({
  program,
  onExpand
}: {
  onExpand: () => void;
  program: AudioCompanionProgram;
}) {
  const previewCopy = getAudioRecommendationCopy(program);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackClaimRef = useRef<AudioPlaybackClaim | null>(null);
  const audioUrl = useMemo(() => assetUrl(program.audioSrc, { versioned: false }), [program.audioSrc]);
  const [audioMissing, setAudioMissing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(program.durationSeconds);
  const [isPlaying, setIsPlaying] = useState(false);
  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;

  const startPlayback = () => {
    const audio = audioRef.current;

    if (!audio || audioMissing) {
      return;
    }

    releasePlaybackClaim(playbackClaimRef);
    const claim = claimAudioPlayback(() => {
      audio.pause();
      playbackClaimRef.current = null;
      setIsPlaying(false);
    });
    playbackClaimRef.current = claim;

    void audio.play().then(() => {
      if (claim.isCurrent()) {
        setIsPlaying(true);
      }
    }).catch(() => {
      claim.release();
      if (playbackClaimRef.current === claim) {
        playbackClaimRef.current = null;
      }
      setIsPlaying(false);
    });
  };

  useEffect(() => {
    releasePlaybackClaim(playbackClaimRef);
    const audio = audioRef.current;

    if (audio) {
      audio.pause();
      audio.load();
    }

    setAudioMissing(false);
    setCurrentTime(0);
    setDuration(program.durationSeconds);
    setIsPlaying(false);

    const autoPlayTimer = window.setTimeout(() => startPlayback(), 80);

    return () => {
      window.clearTimeout(autoPlayTimer);
      releasePlaybackClaim(playbackClaimRef);
      audioRef.current?.pause();
    };
  }, [audioUrl, program.durationSeconds, program.id]);

  const togglePlayback = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const audio = audioRef.current;

    if (!audio || audioMissing) {
      return;
    }

    if (isPlaying) {
      releasePlaybackClaim(playbackClaimRef);
      audio.pause();
      setIsPlaying(false);
      return;
    }

    startPlayback();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onExpand();
    }
  };

  return (
    <article
      className={clsx("night-audio-preview-dock", `night-audio-preview-dock--${program.accent}`)}
      role="button"
      tabIndex={0}
      aria-label={`展开播放 ${previewCopy.title}`}
      onClick={onExpand}
      onKeyDown={handleKeyDown}
    >
      <span className="night-audio-preview-dock__glow" aria-hidden="true" />
      <span
        className={clsx(
          "night-audio-preview-dock__cover",
          `night-audio-preview-dock__cover--${program.accent}`
        )}
        aria-hidden="true"
      >
        <AudioProgramCardIcon programId={program.id} />
      </span>
      <span className="night-audio-preview-dock__copy">
        <small>{previewCopy.category}</small>
        <strong>{previewCopy.title}</strong>
        <span>{audioMissing ? "等待音频文件" : previewCopy.details[0]}</span>
        <span className="night-audio-preview-dock__bar" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </span>
      </span>
      <span className="night-audio-preview-dock__meta" aria-hidden="true">
        {formatAudioTime(currentTime)} / {program.durationLabel}
      </span>
      <button
        className="night-audio-preview-dock__play"
        type="button"
        disabled={audioMissing}
        aria-label={isPlaying ? "暂停预听" : "播放预听"}
        onClick={togglePlayback}
      >
        {isPlaying ? <Pause size={30} strokeWidth={2.1} /> : <Play size={30} strokeWidth={2.1} />}
      </button>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onDurationChange={(event) => {
          const nextDuration = event.currentTarget.duration;
          setDuration(Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : program.durationSeconds);
        }}
        onEnded={() => {
          releasePlaybackClaim(playbackClaimRef);
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onError={() => {
          releasePlaybackClaim(playbackClaimRef);
          setAudioMissing(true);
          setIsPlaying(false);
        }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
      />
    </article>
  );
}

type AudioPlayerModeId = "music" | "content" | "brief";
type AudioPlayerModeIcon = "music" | "podcast" | "radio" | "newspaper";

interface AudioPlayerModeCard {
  id: AudioPlayerModeId;
  icon: AudioPlayerModeIcon;
  isActive: boolean;
  label: string;
  targetProgramId?: AudioCompanionProgramId;
}

function getAudioPlayerModeCards(program: AudioCompanionProgram): AudioPlayerModeCard[] {
  const isMusic = program.id === "commute-playlist";
  const isBrief = program.id === "market-brief";
  const activeMode: AudioPlayerModeId = isMusic ? "music" : isBrief ? "brief" : "content";

  return [
    {
      id: "music",
      icon: "music",
      isActive: activeMode === "music",
      label: "驾趣音乐",
      targetProgramId: "commute-playlist"
    },
    {
      id: "content",
      icon: "podcast",
      isActive: activeMode === "content",
      label: "AI 播客",
      targetProgramId: "tech-morning"
    },
    {
      id: "brief",
      icon: "radio",
      isActive: activeMode === "brief",
      label: "快讯快报",
      targetProgramId: "market-brief"
    }
  ];
}

function AudioPlayerModeCardIcon({
  icon
}: {
  icon: AudioPlayerModeIcon;
}) {
  const iconProps = {
    size: 42,
    strokeWidth: 1.8
  };

  if (icon === "music") {
    return <Music2 {...iconProps} />;
  }

  if (icon === "radio") {
    return <Radio {...iconProps} />;
  }

  if (icon === "newspaper") {
    return <Newspaper {...iconProps} />;
  }

  return <Podcast {...iconProps} />;
}

function createAudioSubtitleLines(text: string) {
  const chunks = text
    .replace(/([，。；！？])/g, "$1|")
    .split("|")
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  const lines: string[] = [];
  let buffer = "";

  chunks.forEach((chunk) => {
    if (!buffer) {
      buffer = chunk;
      return;
    }

    if (buffer.length + chunk.length <= 18 || buffer.length < 8) {
      buffer += chunk;
      return;
    }

    lines.push(buffer);
    buffer = chunk;
  });

  if (buffer) {
    lines.push(buffer);
  }

  return lines.length > 0 ? lines : [text];
}

function getAudioSubtitleIndex(lineCount: number, currentTime: number, duration: number) {
  if (lineCount <= 1 || duration <= 0) {
    return 0;
  }

  const ratio = Math.min(0.999, Math.max(0, currentTime / duration));
  return Math.min(lineCount - 1, Math.floor(ratio * lineCount));
}

function getAudioSubtitleWindow(lines: string[], activeIndex: number) {
  return [-1, 0, 1]
    .map((offset) => ({
      line: lines[activeIndex + offset],
      offset
    }))
    .filter((item): item is { line: string; offset: number } => Boolean(item.line));
}

function AudioPlayerPanel({
  program,
  onBack,
  onSelectProgram
}: {
  onBack: () => void;
  onSelectProgram: (programId: AudioCompanionProgramId) => void;
  program: AudioCompanionProgram;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackClaimRef = useRef<AudioPlaybackClaim | null>(null);
  const audioUrl = useMemo(() => assetUrl(program.audioSrc, { versioned: false }), [program.audioSrc]);
  const [audioMissing, setAudioMissing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(program.durationSeconds);
  const [isPlaying, setIsPlaying] = useState(false);
  const displayCopy = getAudioRecommendationCopy(program);
  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const modeCards = useMemo(() => getAudioPlayerModeCards(program), [program]);
  const subtitleLines = useMemo(
    () => createAudioSubtitleLines(getAudioProgramSubtitleSource(program)),
    [program]
  );
  const activeSubtitleIndex = getAudioSubtitleIndex(subtitleLines.length, currentTime, duration);
  const subtitleWindow = getAudioSubtitleWindow(subtitleLines, activeSubtitleIndex);

  useEffect(() => {
    releasePlaybackClaim(playbackClaimRef);
    audioRef.current?.pause();
    setAudioMissing(false);
    setCurrentTime(0);
    setDuration(program.durationSeconds);
    setIsPlaying(false);
    audioRef.current?.load();
  }, [program.audioSrc, program.durationSeconds, program.id]);

  useEffect(() => () => {
    releasePlaybackClaim(playbackClaimRef);
    audioRef.current?.pause();
  }, []);

  const togglePlayback = () => {
    const audio = audioRef.current;

    if (!audio || audioMissing) {
      return;
    }

    if (isPlaying) {
      releasePlaybackClaim(playbackClaimRef);
      audio.pause();
      setIsPlaying(false);
      return;
    }

    const claim = claimAudioPlayback(() => {
      audio.pause();
      playbackClaimRef.current = null;
      setIsPlaying(false);
    });
    playbackClaimRef.current = claim;

    void audio.play().then(() => {
      if (claim.isCurrent()) {
        setIsPlaying(true);
      }
    }).catch(() => {
      claim.release();
      if (playbackClaimRef.current === claim) {
        playbackClaimRef.current = null;
      }
      setIsPlaying(false);
    });
  };

  const handleModeCardClick = (mode: AudioPlayerModeCard) => {
    if (mode.targetProgramId && mode.targetProgramId !== program.id) {
      onSelectProgram(mode.targetProgramId);
    }
  };

  return (
    <article className="night-audio-player" aria-label="播客播放中">
      <span className="night-audio-player__surface" aria-hidden="true" />

      <div className="night-audio-player__topbar">
        <button
          className="night-audio-player__close"
          type="button"
          onClick={onBack}
          aria-label="返回上一页"
        >
          ×
        </button>
      </div>

      <div className="night-audio-player__station">
        <span className="night-audio-player__band">FM</span>
        <h2>AI 伴行电台</h2>
      </div>

      <AudioEvaVisual className="night-audio-player__hero" />

      <div className="night-audio-player__mode-grid" aria-label="播放模式">
        {modeCards.map((mode) => (
          <button
            key={mode.id}
            className={clsx(
              "night-audio-player__mode-card",
              mode.isActive && "night-audio-player__mode-card--active",
              mode.isActive && `night-audio-player__mode-card--active-${program.accent}`
            )}
            type="button"
            onClick={() => handleModeCardClick(mode)}
            aria-label={mode.label}
            aria-pressed={mode.isActive}
          >
            <AudioPlayerModeCardIcon icon={mode.icon} />
            <span>{mode.label}</span>
          </button>
        ))}
      </div>

      <div className="night-audio-player__main">
        <span className="night-audio-player__category">{displayCopy.category}</span>
        <h3>{displayCopy.title}</h3>
        <div className="night-audio-player__subtitle-wheel" aria-label="播报字幕">
          {subtitleWindow.map((subtitle) => (
            <span
              key={`${subtitle.offset}-${subtitle.line}`}
              className={clsx(
                "night-audio-player__subtitle-line",
                subtitle.offset === 0 && "night-audio-player__subtitle-line--active",
                subtitle.offset < 0 && "night-audio-player__subtitle-line--previous",
                subtitle.offset > 0 && "night-audio-player__subtitle-line--next"
              )}
            >
              {subtitle.line}
            </span>
          ))}
        </div>
      </div>

      <div className="night-audio-player__timeline">
        <span>{formatAudioTime(currentTime)}</span>
        <div className="night-audio-player__bar" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
        <span>{formatAudioTime(duration)}</span>
      </div>

      <div className="night-audio-player__control-row" aria-label="播放控制">
        <button className="night-audio-player__control" type="button" aria-label="播放列表">
          <ListMusic size={36} strokeWidth={1.8} />
        </button>
        <button className="night-audio-player__control" type="button" aria-label="上一段">
          <SkipBack size={38} strokeWidth={1.9} />
        </button>
        <button
          className="night-audio-player__control night-audio-player__control--primary"
          type="button"
          onClick={togglePlayback}
          disabled={audioMissing}
          aria-label={isPlaying ? "暂停播客" : "播放播客"}
        >
          {isPlaying ? <Pause size={42} strokeWidth={2.1} /> : <Play size={42} strokeWidth={2.1} />}
        </button>
        <button className="night-audio-player__control" type="button" aria-label="下一段">
          <SkipForward size={38} strokeWidth={1.9} />
        </button>
        <button className="night-audio-player__control" type="button" aria-label="收藏">
          <Heart size={38} strokeWidth={1.8} />
        </button>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onDurationChange={(event) => {
          const nextDuration = event.currentTarget.duration;
          setDuration(Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : program.durationSeconds);
        }}
        onEnded={() => {
          releasePlaybackClaim(playbackClaimRef);
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onError={() => {
          releasePlaybackClaim(playbackClaimRef);
          setAudioMissing(true);
          setIsPlaying(false);
        }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
      />
    </article>
  );
}

function AudioAnswerPanel({
  onFallbackSpeak,
  onFinished
}: {
  onFallbackSpeak: (text: string) => void;
  onFinished: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrl = useMemo(() => assetUrl(AUDIO_COMPANION_ANSWER.audioSrc, { versioned: false }), []);
  const fallbackStartedRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  const playbackClaimRef = useRef<AudioPlaybackClaim | null>(null);
  const playRequestedRef = useRef(false);
  const [localAudioMissing, setLocalAudioMissing] = useState(false);

  useEffect(() => {
    onFinishedRef.current = onFinished;
  }, [onFinished]);

  useEffect(() => {
    fallbackStartedRef.current = false;
    playRequestedRef.current = false;
    setLocalAudioMissing(false);

    const timeoutId = window.setTimeout(() => onFinishedRef.current(), 7600);
    return () => {
      window.clearTimeout(timeoutId);
      releasePlaybackClaim(playbackClaimRef);
      audioRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    if (!localAudioMissing) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => onFinishedRef.current(), 4300);
    return () => window.clearTimeout(timeoutId);
  }, [localAudioMissing]);

  const handleAudioFallback = () => {
    if (fallbackStartedRef.current) {
      return;
    }

    fallbackStartedRef.current = true;
    releasePlaybackClaim(playbackClaimRef);
    audioRef.current?.pause();
    setLocalAudioMissing(true);
    onFallbackSpeak(AUDIO_COMPANION_ANSWER.text);
  };

  const playAnswerAudioOnce = (audio: HTMLAudioElement) => {
    if (playRequestedRef.current) {
      return;
    }

    playRequestedRef.current = true;
    const claim = claimAudioPlayback(() => {
      audio.pause();
      playbackClaimRef.current = null;
    });
    playbackClaimRef.current = claim;
    void audio.play().catch(() => {
      claim.release();
      if (playbackClaimRef.current === claim) {
        playbackClaimRef.current = null;
      }
      handleAudioFallback();
    });
  };

  return (
    <article className="night-audio-answer" aria-label="EVA 简单问答">
      <span className="night-audio-answer__surface" aria-hidden="true" />
      <AudioEvaVisual className="night-audio-answer__eva" />
      <div className="night-audio-answer__copy">
        <span className="night-audio-demo__eyebrow">
          <Podcast size={22} />
          EVA 回答
        </span>
        <h2>这段讲的什么意思</h2>
        <p>{AUDIO_COMPANION_ANSWER.text}</p>
        <small>
          {localAudioMissing
            ? "本地 TTS 文件不可用，已切换到实时 TTS 播报"
            : "正在播放本地生成的 TTS 音频"}
        </small>
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onCanPlayThrough={(event) => playAnswerAudioOnce(event.currentTarget)}
        onEnded={() => {
          releasePlaybackClaim(playbackClaimRef);
          onFinishedRef.current();
        }}
        onError={handleAudioFallback}
      />
    </article>
  );
}

function AudioSimilarPanel({
  onFallbackSpeak,
  onSelectProgram
}: {
  onFallbackSpeak: (text: string) => void;
  onSelectProgram: (programId: AudioCompanionProgramId) => void;
}) {
  const programs = AUDIO_COMPANION_SIMILAR_PROGRAM_IDS.map(getAudioCompanionProgram);
  const similarDetails: Record<AudioCompanionProgramId, string[]> = {
    "tech-morning": [],
    "commute-playlist": [],
    "market-brief": [],
    "vision-pro-weekly": ["Vision Pro 生态观察", "销量、内容生态与开发者反馈"],
    "ai-hardware-daily": ["端侧模型和芯片供应链", "手机、眼镜、座舱串起来听"],
    "creator-economy": ["从播客到空间内容", "AI 内容生成与新设备入口"],
    "nvidia-generated": []
  };
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrl = useMemo(() => assetUrl(AUDIO_COMPANION_SIMILAR_RECOMMENDATION.audioSrc, { versioned: false }), []);
  const fallbackStartedRef = useRef(false);
  const playbackClaimRef = useRef<AudioPlaybackClaim | null>(null);
  const playRequestedRef = useRef(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [localAudioMissing, setLocalAudioMissing] = useState(false);

  useEffect(() => () => {
    releasePlaybackClaim(playbackClaimRef);
    audioRef.current?.pause();
  }, []);

  const handleAudioFallback = () => {
    if (fallbackStartedRef.current) {
      return;
    }

    fallbackStartedRef.current = true;
    releasePlaybackClaim(playbackClaimRef);
    audioRef.current?.pause();
    setPlaybackBlocked(false);
    setLocalAudioMissing(true);
    onFallbackSpeak(AUDIO_COMPANION_SIMILAR_RECOMMENDATION.text);
  };

  const playSimilarAudioOnce = (audio: HTMLAudioElement) => {
    if (playRequestedRef.current) {
      return;
    }

    playRequestedRef.current = true;
    const claim = claimAudioPlayback(() => {
      audio.pause();
      playbackClaimRef.current = null;
    });
    playbackClaimRef.current = claim;
    void audio.play().catch(() => {
      claim.release();
      if (playbackClaimRef.current === claim) {
        playbackClaimRef.current = null;
      }
      setPlaybackBlocked(true);
    });
  };

  return (
    <article className="night-audio-similar" aria-label="类似播客推荐">
      <span className="night-audio-similar__surface" aria-hidden="true" />
      <AudioEvaVisual className="night-audio-similar__visual" />
      <div className="night-audio-similar__header">
        <span className="night-audio-demo__eyebrow">
          <Radio size={22} />
          类似播客
        </span>
        <h2>还有这些可以继续听</h2>
        <p>
          {localAudioMissing
            ? "本地推荐语音不可用，已切换到实时 TTS。"
            : playbackBlocked
              ? "已为你生成三档类似播客推荐。"
              : "正在播放本地生成的类似播客推荐。"}
        </p>
      </div>
      <div className="night-audio-similar__list">
        {programs.map((program) => (
          <button
            key={program.id}
            className={clsx("night-audio-similar__item", `night-audio-similar__item--${program.accent}`)}
            type="button"
            onClick={() => onSelectProgram(program.id)}
          >
            <AudioProgramArtwork compact program={program} />
            <span className="night-audio-similar__copy">
              <strong>{program.title}</strong>
              <span className="night-audio-similar__details">
                {(similarDetails[program.id].length > 0 ? similarDetails[program.id] : [program.subtitle, program.description]).map((detail) => (
                  <small key={detail}>{detail}</small>
                ))}
              </span>
            </span>
            <span className="night-audio-similar__duration" aria-hidden="true">
              {program.durationLabel}
            </span>
          </button>
        ))}
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onCanPlayThrough={(event) => playSimilarAudioOnce(event.currentTarget)}
        onEnded={() => releasePlaybackClaim(playbackClaimRef)}
        onError={handleAudioFallback}
      />
    </article>
  );
}

function AudioGenerationPanel({
  introFinished,
  onActivateWaitingMusic,
  runId,
  onStartGeneratedProgram,
  onStopWaitingMusic
}: {
  introFinished: boolean;
  onActivateWaitingMusic: () => boolean;
  runId: number;
  onStartGeneratedProgram: () => void;
  onStopWaitingMusic: () => void;
}) {
  const playRequestedRef = useRef(false);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [songMissing, setSongMissing] = useState(false);
  const [waitingAudioReady, setWaitingAudioReady] = useState(false);

  useEffect(() => {
    playRequestedRef.current = false;
    setCompletedSteps(0);
    setGenerationProgress(0);
    setReady(false);
    setSongMissing(false);
    setWaitingAudioReady(false);

    const startedAt = window.performance.now();
    const timers = AUDIO_COMPANION_GENERATION_STEPS.map((step, index) =>
      window.setTimeout(() => {
        setCompletedSteps(index + 1);

        if (index === AUDIO_COMPANION_GENERATION_STEPS.length - 1) {
          setReady(true);
          setGenerationProgress(100);
        }
      }, step.delayMs)
    );
    const progressTimer = window.setInterval(() => {
      const elapsedMs = window.performance.now() - startedAt;
      const nextProgress = Math.min(100, Math.round((elapsedMs / AUDIO_COMPANION_GENERATION_TOTAL_MS) * 100));
      setGenerationProgress(nextProgress);
    }, 120);
    const waitingAudioFallbackTimer = window.setTimeout(
      () => setWaitingAudioReady(true),
      AUDIO_COMPANION_WAITING_MUSIC_FALLBACK_MS
    );

    return () => {
      timers.forEach(window.clearTimeout);
      window.clearInterval(progressTimer);
      window.clearTimeout(waitingAudioFallbackTimer);
      onStopWaitingMusic();
    };
  }, [onStopWaitingMusic, runId]);

  useEffect(() => {
    if (!introFinished) {
      return;
    }

    setWaitingAudioReady(true);
  }, [introFinished]);

  useEffect(() => {
    if (!ready) {
      return;
    }

    playRequestedRef.current = true;
    onStopWaitingMusic();
  }, [onStopWaitingMusic, ready]);

  useEffect(() => {
    if (ready || songMissing || playRequestedRef.current || !waitingAudioReady) {
      return;
    }

    playRequestedRef.current = true;
    const activated = onActivateWaitingMusic();

    if (!activated) {
      setSongMissing(true);
    }
  }, [onActivateWaitingMusic, ready, songMissing, waitingAudioReady]);

  return (
    <article className="night-audio-generation" aria-label="AI 播客生成">
      <span className="night-audio-generation__surface" aria-hidden="true" />
      <AudioEvaVisual className="night-audio-generation__visual" />
      <div className="night-audio-generation__header">
        <span className="night-audio-demo__eyebrow">
          <Sparkles size={22} />
          AI 播客生成
        </span>
        <h2>新的播客内容正在生成</h2>
        <p>
          {ready
            ? "生成完成，可以开始播放新的短总结。"
            : songMissing
              ? "等待音乐暂不可用，生成流程仍在继续。"
              : `等待期间播放 ${AUDIO_COMPANION_WAITING_SONG.title}`}
        </p>
      </div>

      <div
        className="night-audio-generation__progress"
        role="progressbar"
        aria-label="AI 内容生成进度"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={generationProgress}
      >
        <span className="night-audio-generation__progress-track" aria-hidden="true">
          <span style={{ width: `${generationProgress}%` }} />
        </span>
        <strong>{generationProgress}%</strong>
      </div>

      <div className="night-audio-generation__steps">
        {AUDIO_COMPANION_GENERATION_STEPS.map((step, index) => {
          const isComplete = completedSteps > index;
          const isSynthesis = step.id === "synthesis";
          const isActive = !isComplete && completedSteps === index;
          const stepLabel = isComplete ? step.completeLabel : step.label;

          return (
            <div
              key={step.id}
              className={clsx(
                "night-audio-generation__step",
                isActive && "night-audio-generation__step--active",
                isComplete && "night-audio-generation__step--complete"
              )}
            >
              <span className="night-audio-generation__check" aria-hidden="true">
                <span className="night-audio-generation__check-value">
                  {isComplete ? <Check size={38} strokeWidth={2.7} /> : index + 1}
                </span>
              </span>
              <span className="night-audio-generation__step-copy">
                <span className="night-audio-generation__step-index">Step {index + 1}</span>
                <strong>
                  {stepLabel}
                </strong>
                <small>
                  {isSynthesis && ready ? "已就绪" : isComplete ? "已完成" : isActive ? "进行中" : "等待中"}
                </small>
              </span>
            </div>
          );
        })}
      </div>

      {ready ? (
        <button
          className="night-audio-generation__start"
          type="button"
          onClick={onStartGeneratedProgram}
        >
          开始播放
        </button>
      ) : null}

    </article>
  );
}

function AudioEvaVisual({
  className
}: {
  className: string;
}) {
  return (
    <div className={clsx("night-audio-eva-visual", className)} aria-hidden="true">
      <span className="night-audio-eva-visual__ring night-audio-eva-visual__ring--outer" />
      <span className="night-audio-eva-visual__ring night-audio-eva-visual__ring--middle" />
      <span className="night-audio-eva-visual__ring night-audio-eva-visual__ring--inner" />
      <img
        className="night-audio-eva-visual__image"
        src={AUDIO_COMPANION_EVA_HEADPHONES_SRC}
        alt=""
        draggable={false}
      />
    </div>
  );
}

function releasePlaybackClaim(playbackClaimRef: MutableRefObject<AudioPlaybackClaim | null>) {
  playbackClaimRef.current?.release();
  playbackClaimRef.current = null;
}

function fadeAudioVolume(
  audio: HTMLAudioElement,
  targetVolume: number,
  fadeMs: number,
  timerRef: MutableRefObject<number | null>
) {
  if (timerRef.current !== null) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }

  const safeTargetVolume = Math.min(1, Math.max(0, targetVolume));
  const startVolume = audio.volume;
  const startedAt = window.performance.now();

  if (fadeMs <= 0) {
    audio.volume = safeTargetVolume;
    return;
  }

  timerRef.current = window.setInterval(() => {
    const progress = Math.min(1, (window.performance.now() - startedAt) / fadeMs);
    audio.volume = startVolume + (safeTargetVolume - startVolume) * progress;

    if (progress >= 1 && timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, 40);
}

function formatAudioTime(seconds: number) {
  const safeSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = Math.floor(safeSeconds % 60);

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}
